// InvitationSection.jsx
import React, { useState } from 'react';
import apiClient from '../../../../../services/apiClient';
import './InvitationSection.css';

import placesTypes from '../../../../../data/placetype.json';


const getPlaceTranslate = (plType)=>{
  if(placesTypes.translate[plType]) return placesTypes.translate[plType][0];
  return plType;  
}

const InvitationSection = ({ application, onStatusUpdate }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReestablish, setShowReestablish] = useState(false);

  // Проверяем, есть ли активное приглашение
  const hasActiveInvite = application.invite && 
    application.invite.inviteDate && 
    !application.invite.isInviteAccepted && 
    !application.invite.isInviteDeclined;

  // Проверяем, можно ли восстановить заявку (отклонена не более 24 часов назад)
  const canReestablish = application.invite && 
    application.invite.isInviteDeclined && 
    application.invite.expiryDate && 
    new Date(application.invite.expiryDate).getTime() > Date.now();

  // Проверяем, можно ли отказаться от принятого приглашения
  const canCancelAccepted = application.invite && 
    application.invite.isInviteAccepted && 
    application.invite.expiryDate && 
    new Date(application.invite.expiryDate).getTime() > Date.now();

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получение информации о садике из выбранных
  const getKindergartenInfo = () => {
    if (!application.invite?.kindergartenId) return null;
    
    const kg = application.selectedKindergartens?.list?.find(
      kg => kg._id === application.invite.kindergartenId
    );
    
    return kg || { name: 'Невідомий садочок', address: '' };
  };

  // Принятие приглашения
  const handleAcceptInvite = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      await apiClient.post(`invite/request/${application.id}/accept_request_invite`);
      onStatusUpdate();
    } catch (error) {
      console.error('Помилка при прийнятті запрошення:', error);
      alert('Не вдалося прийняти запрошення. Спробуйте ще раз.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Отклонение приглашения
  const handleDeclineInvite = async () => {
    if (isProcessing) return;
    
    if (!window.confirm('Ви впевнені, що хочете відхилити запрошення? Після цього заявку можна буде відновити протягом 24 годин.')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await apiClient.post(`invite/request/${application.id}/decline_request_invite`);
      onStatusUpdate();
      setShowReestablish(true);
    } catch (error) {
      console.error('Помилка при відхиленні запрошення:', error);
      alert('Не вдалося відхилити запрошення. Спробуйте ще раз.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Отказ от принятого приглашения
  const handleCancelAccepted = async () => {
    if (isProcessing) return;
    
    if (!window.confirm('Ви впевнені, що хочете скасувати прийняте запрошення? Після цього заявку можна буде відновити протягом 24 годин.')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await apiClient.post(`invite/request/${application.id}/decline_request_invite?force=true`);
      onStatusUpdate();
      setShowReestablish(true);
    } catch (error) {
      console.error('Помилка при скасуванні запрошення:', error);
      alert('Не вдалося скасувати запрошення. Спробуйте ще раз.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Восстановление заявки
  const handleReestablish = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      await apiClient.post(`invite/reestablish/${application.id}`);
      onStatusUpdate();
      setShowReestablish(false);
    } catch (error) {
      console.error('Помилка при відновленні заявки:', error);
      alert('Не вдалося відновити заявку. Спробуйте ще раз.');
    } finally {
      setIsProcessing(false);
    }
  };

  const kindergartenInfo = getKindergartenInfo();


  // Показываем компонент если есть приглашение (активное, принятое или отклоненное)
  if (!application.invite) {
    return null;
  }

  return (
    <div className="invitation-section">
      <div className="invitation-header">
        <h3 className="invitation-title">Запрошення до дитячого садочка</h3>
        <div className="invitation-status-badge">
          {application.invite.isInviteAccepted ? (
            <span className="badge status-accepted">Запрошення прийнято</span>
          ) : application.invite.isInviteDeclined ? (
            <span className="badge status-declined">Запрошення відхилено</span>
          ) : (
            <span className="badge status-pending">Очікує підтвердження</span>
          )}
        </div>
      </div>

      <div className="invitation-details">
        <div className="invitation-kindergarten">
          <h4 className="kg-name">{kindergartenInfo?.name || 'Садочок'}</h4>
          <p className="kg-address">{kindergartenInfo?.address || ''}</p>
        </div>

        <div className="invitation-meta">
          <div className="meta-item">
            <span className="meta-label">Група:</span>
            <span className="meta-value">{application.invite.groupNumber || 'Не вказано'}</span>
          </div>
          
          <div className="meta-item">
            <span className="meta-label">Тип місця:</span>
            <span className="meta-value">
              { getPlaceTranslate(application.invite.placeType) }
            </span>
          </div>

          <div className="meta-item">
            <span className="meta-label">Дата початку:</span>
            <span className="meta-value">
              {application.invite.inviteDate ? formatDate(application.invite.inviteDate) : 'Не вказано'}
            </span>
          </div>

          <div className="meta-item">
            <span className="meta-label">Дійсне до:</span>
            <span className="meta-value">
              {application.invite.expiryDate ? formatDate(application.invite.expiryDate) : 'Не вказано'}
            </span>
          </div>
        </div>

        {/* Кнопки для активного приглашения */}
        {!application.invite.isInviteAccepted && !application.invite.isInviteDeclined && (
          <div className="invitation-actions">
            <button
              className="btn-accept"
              onClick={handleAcceptInvite}
              disabled={isProcessing}
            >
              {isProcessing ? 'Обробка...' : 'Прийняти запрошення'}
            </button>
            <button
              className="btn-decline"
              onClick={handleDeclineInvite}
              disabled={isProcessing}
            >
              {isProcessing ? 'Обробка...' : 'Відхилити запрошення'}
            </button>
          </div>
        )}



        {application.invite.isInviteAccepted && canCancelAccepted && (
             <div className="accepted-info">
             Ви прийняли запрошення. Ви можете скасувати його до {formatDate(application.invite.expiryDate)}.
            </div>
        )}

        {/* Кнопка для отказа от принятого приглашения */}
        {application.invite.isInviteAccepted && canCancelAccepted && (
          <div className="invitation-actions">
            <button
              className="btn-decline"
              onClick={handleCancelAccepted}
              disabled={isProcessing}
            >
              {isProcessing ? 'Обробка...' : 'Скасувати запрошення'}
            </button>
          </div>
        )}

        {/* Информация о принятом приглашении с истекшим сроком */}
        {application.invite.isInviteAccepted && !canCancelAccepted && (
          <div className="accepted-expired-info">
            <p>Запрошення прийнято. Термін дії запрошення закінчився.</p>
          </div>
        )}

        {/* Восстановление заявки */}
        {(application.invite.isInviteDeclined || showReestablish) && canReestablish && (
          <div className="reestablish-section">
            <p className="reestablish-info">
              Заявку відхилено. Ви можете відновити її протягом 24 годин.
            </p>
            <button
              className="btn-reestablish"
              onClick={handleReestablish}
              disabled={isProcessing}
            >
              {isProcessing ? 'Обробка...' : 'Відновити заявку'}
            </button>
          </div>
        )}

        {/* Информация об автоматическом отклонении */}
        {application.invite.expiryDate && new Date(application.invite.expiryDate).getTime() < Date.now() && 
         !application.invite.isInviteAccepted && !application.invite.isInviteDeclined && (
          <div className="expiry-warning">
            <p>Термін дії запрошення закінчився. Заявка автоматично відхилена.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationSection;